import * as Sentry from '@sentry/nextjs';
import type { ActionItem } from '@/lib/queries/types';
import { RECOVERABLE_STAGED_AGE_MS, type StagedTarget } from '@/lib/chat/recover-staged';
import { MOBILE_MQ } from '@/lib/viewport/client';

/**
 * Is the approval card Mucka promised actually on the screen?
 *
 * ── Why this exists ─────────────────────────────────────────────────────────
 *
 * Two fixes have shipped for "Mucka says tap Send, there is no Send button"
 * (#2830 recovery from the row, #2856 the card in the phone bar), each written
 * from reading the code. It went on happening: seven voice calls across five
 * accounts between 21 and 24 Sep, the last two after both fixes (TKT-1346,
 * "still no send button there").
 *
 * In every one of them `engine_actions.first_served_at` is 1–3 seconds after
 * the card was written, so the row reached the browser. What nobody can see is
 * what happened next — whether recovery attached it to the thread at all, or
 * attached it to a surface that was not being drawn (the panel closed, the
 * phone bar minimised during a live call). `first_seen_at` cannot answer it:
 * only the Actions surfaces fire that beacon, never the chat card.
 *
 * The own-brain path has its own probe (`voice-brain/events.ts`). The standard
 * ElevenLabs path — the one every customer is on — had none, so this is it.
 *
 * 🚨 Browser code: reports through `Sentry.captureMessage`, never
 * `console.error`, which reaches nobody from a client component (CLAUDE.md #13).
 */

/** How long a card may be waiting before its absence is worth reporting —
 *  long enough for realtime, the 5s poll and the attach to have all run. */
export const STAGED_PROBE_GRACE_MS = 10_000;

export type StagedProbeStage = 'not-attached' | 'attached-not-drawn' | 'drawn-off-screen';

/**
 * Null when the card is on screen; otherwise which part of the path lost it.
 *
 * `drawn-off-screen` (TKT-1472): rendered inside a showing surface, but the
 * Send button was never inside the visible viewport. That covers being
 * scrolled out of the bar's list, sitting behind the keyboard, or being in the
 * panel while the panel is pushed below the screen.
 */
export function classifyStagedCard(
  id: string,
  messages: readonly StagedTarget[],
  visible: boolean,
  inSurface = false,
): StagedProbeStage | null {
  if (visible) return null;
  if (inSurface) return 'drawn-off-screen';
  const attached = messages.some(m => m.stagedActions?.some(sa => sa.id === id));
  return attached ? 'attached-not-drawn' : 'not-attached';
}

/**
 * The width at and below which the panel is a slide-in overlay rather than a
 * column of the page. Mirrors `@media (max-width: 1024px)` in globals.css
 * (".mucka-panel { position: fixed … transform: translateX(100%) }"); the
 * staged-probe test reads the stylesheet so the two cannot drift apart.
 */
export const PANEL_OVERLAY_MQ = '(max-width: 1024px)';

/**
 * Is the panel on screen whatever `isOpen` says?
 *
 * 🚨 On a desktop wider than 1024px the panel is not a drawer at all: it is
 * the third column of `.shell.has-panel`, always showing, and `isOpen` stays
 * false because nothing ever "opened" it. MUCKA-WEB-AC (25 Sep) was this:
 * an owner started a voice call from the docked panel, the card drew in it,
 * and he tapped Send 47 seconds later — while the probe reported the card as
 * not on screen because `panel_open` was false. Amplitude has no "Mucka Panel
 * Opened" for him, ever; he has never needed one.
 */
export function isPanelDocked(win: Window = window): boolean {
  if (typeof win.matchMedia !== 'function') return false;
  return !win.matchMedia(PANEL_OVERLAY_MQ).matches && !win.matchMedia(MOBILE_MQ).matches;
}

/**
 * Whether any rendering of the card is on a surface that is showing.
 *
 * 🚨 Not "is it laid out". On a phone the full panel stays mounted and is
 * pushed a screen-height below the viewport, so its copy of the card has real
 * rects while nobody can see it — measured on dev, 24 Sep, `translateY(596px)`.
 * The first cut of this probe asked about rects and would have reported the
 * card as drawn in exactly the case it was built to catch. So a copy counts
 * only inside the bar's OPEN sheet, or inside the panel while it is showing —
 * open as a drawer, or docked as a column (`isPanelDocked`).
 */
export function isStagedCardDrawn(id: string, panelShowing: boolean, doc: Document = document): boolean {
  return surfaceCopies(id, panelShowing, doc).length > 0;
}

function surfaceCopies(id: string, panelShowing: boolean, doc: Document): HTMLElement[] {
  return Array.from(doc.querySelectorAll<HTMLElement>('[data-staged-id]'))
    .filter(el => el.dataset.stagedId === id)
    .filter(el => !!el.closest('.mucka-bar-sheet--open') || (panelShowing && !!el.closest('.mucka-panel')));
}

/** Where a card's Send button sat when the probe last looked. Reported with a
 *  `drawn-off-screen` card so the next one says WHERE, not just "not seen". */
export interface StagedCardPlacement {
  top: number;
  bottom: number;
  /** The visible area: the visual viewport, so a raised keyboard shrinks it. */
  viewTop: number;
  viewBottom: number;
  /** The tightest scrolling or clipping box above the button. */
  clipTop: number | null;
  clipBottom: number | null;
}

/**
 * Is the card's Send button actually inside what the owner can see?
 *
 * 🚨 TKT-1472. `isStagedCardDrawn` asks whether a copy is inside an open
 * surface, which is not the same as visible. The bar draws the card inside
 * its own scrolling list (up to 80dvh), so the copy can be in the open sheet
 * but scrolled out of the list, or behind the Android keyboard. On 7 Oct 2026
 * an owner on the Android app said "I can't see anything at the bottom here"
 * for four minutes, and the probe filed nothing. Whatever it saw, it counted
 * the card as drawn.
 *
 * Measures the actions row (the Send button), not the card body: a long email
 * can show its first line while the button is out of view. A copy counts when
 * its button is inside the visual viewport AND inside every clipping box above
 * it. A rect alone ignores `overflow`, so a card scrolled out of the list still
 * has a rect over some other part of the screen.
 *
 * A zero-size rect means "never laid out" (jsdom, `display: none`), and counts
 * as not visible.
 */
export function stagedCardPlacement(
  id: string,
  panelShowing: boolean,
  doc: Document = document,
  win: Window = window,
): { visible: boolean; inSurface: boolean; placement: StagedCardPlacement | null } {
  const copies = surfaceCopies(id, panelShowing, doc);
  if (!copies.length) return { visible: false, inSurface: false, placement: null };
  const vv = win.visualViewport;
  const viewTop = vv ? vv.offsetTop : 0;
  const viewBottom = viewTop + (vv ? vv.height : win.innerHeight);
  let placement: StagedCardPlacement | null = null;
  for (const copy of copies) {
    const target = (copy.nextElementSibling as HTMLElement | null) ?? copy;
    const r = target.getBoundingClientRect();
    if (r.width <= 0 || r.height <= 0) continue;
    let top = Math.max(r.top, viewTop);
    let bottom = Math.min(r.bottom, viewBottom);
    let clipTop: number | null = null;
    let clipBottom: number | null = null;
    for (let el = target.parentElement; el && el !== doc.body; el = el.parentElement) {
      const overflowY = win.getComputedStyle(el).overflowY;
      if (overflowY === 'visible') continue;
      const c = el.getBoundingClientRect();
      if (clipTop === null) { clipTop = c.top; clipBottom = c.bottom; }
      top = Math.max(top, c.top);
      bottom = Math.min(bottom, c.bottom);
    }
    placement = { top: r.top, bottom: r.bottom, viewTop, viewBottom, clipTop, clipBottom };
    if (bottom - top > 0) return { visible: true, inSurface: true, placement };
  }
  return { visible: false, inSurface: true, placement };
}

/**
 * Should this card be reported now?
 *
 * The probe asks "did the card EVER reach a surface the owner could see?",
 * not "is it on screen at this instant". Two things answer yes on their own:
 *
 *  - it was seen drawn on an earlier tick. A card the owner saw and then put
 *    away (closed the drawer, folded the bar) was delivered; reporting it
 *    says the pipeline lost a card it did not lose.
 *  - it was seen on a card surface outside the bar and panel, the Actions
 *    page above all: since TKT-1472 the agent sends the owner there for a
 *    card they cannot find. `wasCardSighted` (`card-seen-queue.ts`) carries
 *    that page's own record of what was on screen. MUCKA-WEB-CA was an owner
 *    approving a card from Actions while this probe held it as missing.
 *  - the owner has already pressed Send or Dismiss on it (`acted`). One
 *    account, 25 Sep: the send was attempted 29s after the card was written, failed on
 *    a bounced address, and 16s later the probe reported the card as missing.
 *
 * What a real failure still looks like, and still reports: a card in the
 * thread (or not) that at no tick from its arrival to the grace period and
 * beyond was inside the open bar sheet or a showing panel, and that the owner
 * has not touched — the TKT-1346 shape.
 */
export function shouldReportStagedCard(p: {
  ageMs: number;
  drawnNow: boolean;
  everDrawn: boolean;
  acted: boolean;
}): boolean {
  if (p.drawnNow || p.everDrawn || p.acted) return false;
  return p.ageMs >= STAGED_PROBE_GRACE_MS;
}

/**
 * How long before THIS call started a card may have been made and still count
 * as promised on it. Covers a call that drops and is restarted straight away
 * ("hang on, I'll ring back") without reaching back into an earlier session.
 */
export const STAGED_PROBE_CALL_LOOKBACK_MS = 2 * 60 * 1000;

/**
 * How long the probe has been able to watch a card: from when it was written,
 * or from when this call began, whichever is later. This, not the card's age,
 * is what the grace period is measured against.
 *
 * 🚨 MUCKA-WEB-CN (8 Oct). The lookback above lets a call watch a card made
 * before it started, but the grace was counted from `createdAt`, so such a
 * card was already past it at the first tick, before the 5s poll had had one
 * chance to attach it. Both events were one card, written 29s and then 100s
 * before a call, on a page that had just loaded, and drawn in the docked
 * panel about seven seconds into each call.
 */
export function stagedWatchMs(createdAt: string, callStartedAt: number | null, now: number): number {
  return now - Math.max(Date.parse(createdAt), callStartedAt ?? -Infinity);
}

/**
 * Pending chat cards young enough to watch — from the moment they exist, so a
 * card drawn and put away inside the grace period is remembered as drawn.
 *
 * 🚨 Only cards made during THIS call (`callStartedAt`, less a short
 * lookback). The probe asks "is the card Mucka just promised on screen?", and
 * a card from an earlier call is not one — it was promised to a different
 * session, whose memory of having drawn it (`drawnStagedRef`) went with the
 * page. MUCKA-WEB-B0 (one plumbing firm, 29 Sep) was exactly that: a Send quote card made on
 * a 09:09 call, drawn on it and left unsent, reported as "not on screen" 83
 * minutes later when the owner started a new call to mark a job complete.
 * `RECOVERABLE_STAGED_AGE_MS` is two hours because RECOVERY must survive a
 * reload; it was never the right window for asking whether a promise landed.
 */
export function watchableStagedCards(
  actions: readonly ActionItem[],
  now: number,
  callStartedAt: number | null = null,
): ActionItem[] {
  const floor = callStartedAt === null ? -Infinity : callStartedAt - STAGED_PROBE_CALL_LOOKBACK_MS;
  return actions.filter(a => {
    if (a.ruleType !== 'chat_staged' || a.actionType !== 'staged_tool' || !a.createdAt) return false;
    const at = Date.parse(a.createdAt);
    const age = now - at;
    return Number.isFinite(age) && age >= 0 && age <= RECOVERABLE_STAGED_AGE_MS && at >= floor;
  });
}

export interface StagedProbeContext {
  accountId: string | null;
  mobile: boolean;
  native: boolean;
  panelOpen: boolean;
  panelDocked: boolean;
  messageCount: number;
  /** Where the Send button was, for a card drawn but off screen. */
  placement?: StagedCardPlacement | null;
}

/**
 * How a card that missed the grace period ended up.
 *
 *  · `found_late` — it reached the owner after all: drawn, acted on, or
 *    resolved somewhere else, after the grace period. Delivered, slowly.
 *  · `not_found_during_call` — the call ended with it still neither drawn nor
 *    touched. This is the TKT-1346 shape, the one the probe exists for.
 *
 * 🚨 The probe used to report at the ten-second mark, which could not tell the
 * two apart. The one event after the MUCKA-WEB-AC fixes was Dave's, 6 Oct: not
 * drawn at 13s, approved from the app at 41s, the email sent. Same report as a
 * card nobody ever saw. Same shape as MUCKA-WEB-BA's fix.
 */
export type StagedProbeOutcome = 'found_late' | 'not_found_during_call';

interface StagedSuspect {
  card: ActionItem;
  stage: StagedProbeStage;
  ctx: StagedProbeContext;
  suspectAt: number;
}

/**
 * Cards past the grace period and not yet seen, held until they are found or
 * the call ends. One per MuckaPanel. Reports at most once per card.
 */
export function createStagedSuspects() {
  const suspects = new Map<string, StagedSuspect>();
  return {
    has: (id: string) => suspects.has(id),
    mark(card: ActionItem, stage: StagedProbeStage, ctx: StagedProbeContext, now: number) {
      if (!suspects.has(card.id)) suspects.set(card.id, { card, stage, ctx, suspectAt: now });
    },
    /**
     * Report any suspect the owner has since reached. `stillPending` false means
     * the card left the pending list, which only happens when somebody decided
     * it — from the bar, the Actions page or another device.
     */
    settle(found: (id: string) => boolean, now: number) {
      for (const [id, s] of suspects) {
        if (!found(id)) continue;
        suspects.delete(id);
        reportUndrawnStagedCard(s.card, s.stage, s.ctx, now, 'found_late');
      }
    },
    /** The call ended: everything still held was never found on it. */
    flush(now: number) {
      for (const [id, s] of suspects) {
        suspects.delete(id);
        reportUndrawnStagedCard(s.card, s.stage, s.ctx, now, 'not_found_during_call');
      }
    },
  };
}

export function reportUndrawnStagedCard(
  card: ActionItem,
  stage: StagedProbeStage,
  ctx: StagedProbeContext,
  now: number,
  outcome: StagedProbeOutcome,
) {
  const barOpen = typeof document !== 'undefined' && !!document.querySelector('.mucka-bar-sheet--open');
  console.warn(`[staged-probe] approval card ${stage}: ${outcome}`);
  Sentry.captureMessage('An approval card is waiting but is not on screen', {
    // A card the owner found is a slow path, not a lost promise.
    level: outcome === 'found_late' ? 'info' : 'warning',
    // Split by outcome, so a slow find can never hold open, or hide, a card
    // that was never seen at all.
    fingerprint: ['staged-card-not-on-screen', outcome, stage],
    tags: {
      outcome,
      stage,
      mobile: String(ctx.mobile),
      native: String(ctx.native),
      panel_open: String(ctx.panelOpen),
      panel_docked: String(ctx.panelDocked),
      bar_open: String(barOpen),
      visibility: typeof document !== 'undefined' ? document.visibilityState : 'unknown',
    },
    extra: {
      cardId: card.id,
      accountId: ctx.accountId,
      // From the card's creation to when it was found, or to the call ending.
      ageMs: card.createdAt ? now - Date.parse(card.createdAt) : null,
      messageCount: ctx.messageCount,
      placement: ctx.placement ?? null,
      path: typeof location !== 'undefined' ? location.pathname : null,
    },
  });
}
